/** @prettier */

import {memo, useEffect, useState} from 'react';
import {
  Card,
  CardContent,
  Grid,
  Paper,
  CardHeader,
  Box,
  Stack,
} from '@mui/material';
import {useTranslation} from 'react-i18next';

//MTH component import
import GraphHeader from './GraphAppBar';
import LinearWithValueLabel from '../../LinearProgressWithLabel';
import {StaticDatePicker, StaticComparisonDatePicker} from './DatePicker';
import TextFieldSelectBox from '../../SelectBox';
import {AddButton} from '../../BasicButton';
import {IndoorLineGraphDisplay} from './GraphDisplay';
//type import
import {GraphDataRange, graphProps} from '../../../Type/TypeDef';
//Style import
import '../../../css/common/common.css';
//interface import
import * as apiIF from '../../../Type/api';

import {useInteractJS} from '../../../functions/positionMove';
import {useGetStartEndDate} from '../../../hooks/graph/useGetStartEndDate';
import {useRTChangeStartEndDate} from '../../../hooks/graph/useQtonChangeStartEndDate';
import {makeHyozanIUGraphData} from '../../../functions/makeRTGraphData';
import {iconBlack, pullDownMenu} from '../../../define';
import {makeXLabel} from '../../../functions/graphFunction';

/**
 * Hyozan室内機グラフ
 */
const HyozanIndoorGraph = memo((props: {graphProps: graphProps}) => {
  const trans = useTranslation().t; //翻訳関数
  //prettier-ignore
  const {siteID, graphTitle, graphTarget, graphTargetID, graphType, setGraphParam} = props.graphProps;

  //日付の表示（1日/1週間/1か月/1年/10年）
  const [dateRange, setDateRange] = useState<GraphDataRange>('day');
  //prettier-ignore
  const [trendGraphInsideTemperature, setTrendGraphInsideTemperature] = useState<ApexAxisChartSeries['0']['data']>([]); //折れ線グラフデータ 庫内温度
  //prettier-ignore
  const [trendGraphPresetTemperature, setTrendGraphPresetTemperature] = useState<ApexAxisChartSeries['0']['data']>([]); //折れ線グラフデータ 設定温度
  //prettier-ignore
  const [comparisonTrendGraphInsideTemp, setComparisonTrendGraphInsideTemp] = useState<ApexAxisChartSeries['0']['data']>([]); //折れ線グラフデータ 庫内温度（比較日）
  //prettier-ignore
  const [comparisonTrendGraphPresetTemp, setComparisonTrendGraphPresetTemp] = useState<ApexAxisChartSeries['0']['data']>([]); //折れ線グラフデータ 設定温度（比較日）
  const [xDate, setXDate] = useState<string[] | []>([]); //通常日の日付データ
  const [comparisonXDate, setComparisonXDate] = useState<string[]>([]); //比較日の日付データ
  const [xAxisLabel, setXAxisLabel] = useState<string[]>([]); //x軸に表示するラベル
  //prettier-ignore
  const [comparisonXAxisLabel, setComparisonXAxisLabel] = useState<string[]>([]); //比較グラフのx軸に表示するラベル
  const [activeComparison, setActiveComparison] = useState(false); //比較データ表示判定

  const interact = useInteractJS(); //グラフダイアログの移動に必要な関数

  //prettier-ignore
  //グラフに表示する日付の範囲を取得
  const {timeZone, graphStartDate, graphEndDate, comparisonGraphStartDate, comparisonGraphEndDate, setGraphDispDate, setComparisonGraphDispDate} = useGetStartEndDate(siteID)

  /** 温度グラフデータの凡例名 */
  const selectedItem: (keyof apiIF.resParameters.rtIUStates)[] = [
    'inside-temperature',
    'preset-temperature',
  ];

  //日付や対象が変更されたとき、グラフに描画するデータを再取得
  const {rtStateData, csvFileNameDate, graphDispError, apiWait} =
    useRTChangeStartEndDate({
      siteID,
      graphStartDate,
      graphEndDate,
      graphType,
      timeZone,
      dateRange: dateRange,
      selectedItem,
    });
  const {
    rtStateData: comparisonRtStateData,
    csvFileNameDate: comparisonCsvFileNameDate,
    graphDispError: comparisonGraphDispError,
    apiWait: comparisonApiWait,
  } = useRTChangeStartEndDate({
    siteID: siteID,
    graphStartDate: comparisonGraphStartDate,
    graphEndDate: comparisonGraphEndDate,
    graphType,
    timeZone,
    dateRange: dateRange,
    selectedItem,
  });

  //受信APIデータ変更時、グラフ表示項目変更時にグラフ描画用データを作成する
  useEffect(() => {
    if (rtStateData === null) return;
    const ret = makeHyozanIUGraphData({
      graphType,
      dateRange,
      graphTargetID,
      graphStartDate,
      graphEndDate,
      timeZone,
      rtStateData,
      selectedItem,
      trans,
    });

    setXDate(ret.xLabel);
    setTrendGraphInsideTemperature(ret.yArrayLine[0].data);
    setTrendGraphPresetTemperature(ret.yArrayLine[1].data);
  }, [trans, rtStateData]);

  //比較ボタン押下時に、別の日のグラフ描画用データを作成する
  useEffect(() => {
    if (comparisonRtStateData === null) return;
    const comparisonRet = makeHyozanIUGraphData({
      graphType,
      dateRange,
      graphTargetID,
      graphStartDate: comparisonGraphStartDate,
      graphEndDate: comparisonGraphEndDate,
      timeZone: timeZone,
      rtStateData: comparisonRtStateData,
      selectedItem: selectedItem,
      trans,
    });

    setComparisonXDate(comparisonRet.xLabel);
    setComparisonTrendGraphInsideTemp(comparisonRet.yArrayLine[0].data);
    setComparisonTrendGraphPresetTemp(comparisonRet.yArrayLine[1].data);
  }, [trans, comparisonRtStateData]);

  //x軸に表示するラベルを作成する
  useEffect(() => {
    makeXLabel({
      xDate: xDate,
      comparisonXDate: comparisonXDate,
      dateRange: dateRange,
      graphType: graphType,
      activeComparison: activeComparison,
      setXAxisLabel: setXAxisLabel,
      setComparisonXAxisLabel: setComparisonXAxisLabel,
    });
  }, [xDate, comparisonXDate, activeComparison]);

  /** 比較機能のON/OFFを切り替える関数 */
  const handleOnComparison = () => {
    setActiveComparison(!activeComparison);
  };

  /** グラフ
   * @returns JSX.Element
   */
  const GraphDisplay = () => {
    return (
      <IndoorLineGraphDisplay
        dateRange={dateRange}
        activeComparison={activeComparison}
        csvFileNameDate={
          activeComparison
            ? `${csvFileNameDate},${comparisonCsvFileNameDate}`
            : csvFileNameDate
        }
        csvFileNameTarget={graphTitle}
        graphTarget={graphTarget}
        xAxisLabel={xAxisLabel}
        comparisonXAxisLabel={comparisonXAxisLabel}
        trendGraphInsideTemp={trendGraphInsideTemperature}
        trendGraphPresetTemp={trendGraphPresetTemperature}
        comparisonTrendGraphInsideTemp={comparisonTrendGraphInsideTemp}
        comparisonTrendGraphPresetTemp={comparisonTrendGraphPresetTemp}
      />
    );
  };

  return (
    <Paper
      elevation={10}
      className="graph"
      ref={interact.ref}
      style={{...interact.style}}>
      <Card className="graph__card">
        <CardHeader
          disableTypography
          title={
            <GraphHeader
              setGraphParam={setGraphParam}
              graphTitle={graphTitle}
            />
          }
          className="graph__card__header"
        />
        <CardContent
          //週比較の場合、スクロールバーを表示する
          className={`graph__card__main ${
            dateRange === 'week' && activeComparison
              ? 'graph__card__main--line'
              : ''
          }`}>
          {/* グラフデータ設定欄 */}
          {timeZone !== '' && (
            <Grid
              container
              justifyContent="space-between"
              alignItems="center"
              className="graph__card__main__menu">
              <Grid item xs="auto" container alignItems="center" spacing={2}>
                {/* カレンダー */}
                <Grid item xs="auto">
                  <StaticDatePicker
                    setGraphDisplayDate={setGraphDispDate}
                    timeZone={timeZone}
                    dateRange={dateRange}
                  />
                </Grid>
                {/* 比較用カレンダー */}
                <Grid item xs>
                  {activeComparison ? (
                    <StaticComparisonDatePicker
                      setGraphDisplayDate={setComparisonGraphDispDate}
                      timeZone={timeZone}
                      dateRange={dateRange}
                      onCloseClick={handleOnComparison}
                    />
                  ) : (
                    //比較用カレンダー表示ボタン
                    <AddButton
                      onClick={handleOnComparison}
                      buttonText={trans(
                        'HyozanIndoorGraph.buttonTxt.comparison'
                      )}
                    />
                  )}
                </Grid>
              </Grid>
              <Grid item xs="auto">
                {/* グラフ表示期間設定セレクトボックス */}
                <TextFieldSelectBox
                  label={trans('HyozanIndoorGraph.txtField.label.graphRange')}
                  choices={pullDownMenu.LINE_GRAPH_RANGE}
                  value={dateRange}
                  setValue={setDateRange}
                  id={'graphRangeSelect'}
                  class="graph__card__main__menu__selectBox"
                />
              </Grid>
            </Grid>
          )}
          {/* グラフ */}
          {apiWait || comparisonApiWait ? (
            <Stack justifyContent="center" alignItems="center">
              {graphDispError || comparisonGraphDispError ? (
                <h1 className="graph__card__main__errorMessage">
                  {trans('HyozanIndoorGraph.errMsg.gettingDataFailure')}
                </h1>
              ) : (
                <Box className="graph__card__main__progress">
                  <LinearWithValueLabel />
                </Box>
              )}
            </Stack>
          ) : graphDispError || comparisonGraphDispError ? (
            <Stack justifyContent="center" alignItems="center">
              <h1 className="graph__card__main__errorMessage">
                {trans('HyozanIndoorGraph.errMsg.gettingDataFailure')}
              </h1>
            </Stack>
          ) : (
            <GraphDisplay />
          )}
        </CardContent>
      </Card>
    </Paper>
  );
});
export default HyozanIndoorGraph;
